import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Receipt,
  CreditCard,
  FileCheck,
  Paperclip,
  ExternalLink,
  IdCard,
  FileCheck2,
  Lock,
} from "lucide-react";
import { server } from "../server";

const VerifyStudentFee = () => {
  const { uniqueId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isVerified, setIsVerified] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [verifiedAt, setVerifiedAt] = useState("");

  const fetchReceiptData = async (idToFetch) => {
    if (!idToFetch) {
      setLoading(false);
      setReceiptData(null);
      setAttachments([]);
      setIsVerified(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${server}/fee-collection/verify/${idToFetch}`,
        {
          headers: {
            accept: "*/*",
          },
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          setIsVerified(false);
          setReceiptData(null);
          setAttachments([]);
          setError("Fee payment receipt record not found.");
        } else {
          throw new Error(`Server responded with status ${response.status}`);
        }
      } else {
        const result = await response.json();
        if (result.success && result.verified && result.data) {
          setReceiptData(result.data);
          setAttachments(result.attachments || []);
          setIsVerified(true);
          setVerifiedAt(
            new Date().toLocaleString("en-IN", {
              dateStyle: "medium",
              timeStyle: "short",
            }),
          );
        } else {
          setIsVerified(false);
          setReceiptData(result.data || null);
          setAttachments(result.attachments || []);
          setError(
            "This fee payment receipt could not be verified in the database.",
          );
        }
      }
    } catch (err) {
      console.error("Fee verification error:", err);
      setError(
        "Unable to connect to verification server. Please try again later.",
      );
      setIsVerified(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uniqueId) {
      setSearchInput(uniqueId);
      fetchReceiptData(uniqueId);
    } else {
      setLoading(false);
    }
  }, [uniqueId]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/verify/studentFee/${searchInput.trim()}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const student = receiptData?.student || {};
  const feePayment = receiptData?.feePayment || {};
  const studentFee = feePayment?.studentFee || {};
  const feeStructure = studentFee?.feeStructure || {};

  const totalFee = studentFee.totalAmount || feeStructure.amount || 0;
  const paidFee =
    receiptData?.totalAmountPaid ||
    feePayment.amountPaid ||
    studentFee.paidAmount ||
    0;
  const dueFee = Math.max(0, totalFee - paidFee);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans pb-10 pt-4 px-3 sm:px-6">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Portal Header & Service Switcher */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center space-x-3 w-full md:w-auto">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-primary border border-blue-200 flex items-center justify-center shrink-0">
              <Receipt className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-lg font-bold text-slate-900">
                  Dee Novo Verification Portal
                </h1>
                <span className="bg-emerald-50 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase border border-emerald-200">
                  Official
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Financial Fee Receipt Registry
              </p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-semibold w-full md:w-auto justify-center">
            <Link
              to={uniqueId ? `/verify/idCard/${uniqueId}` : "/verify/idCard"}
              className="px-3 py-1.5 rounded-md text-slate-600 hover:text-primary flex items-center gap-1.5 transition-colors"
            >
              <IdCard className="w-3.5 h-3.5" /> ID Card
            </Link>
            <Link
              to={
                uniqueId
                  ? `/verify/transferCertificate/${uniqueId}`
                  : "/verify/transferCertificate"
              }
              className="px-3 py-1.5 rounded-md text-slate-600 hover:text-primary flex items-center gap-1.5 transition-colors"
            >
              <FileCheck2 className="w-3.5 h-3.5" /> Transfer Cert
            </Link>
            <Link
              to={
                uniqueId
                  ? `/verify/studentFee/${uniqueId}`
                  : "/verify/studentFee"
              }
              className="px-3 py-1.5 rounded-md bg-white text-primary shadow-xs font-bold flex items-center gap-1.5 transition-colors border border-slate-200"
            >
              <Receipt className="w-3.5 h-3.5" /> Fee Receipt
            </Link>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-slate-200 space-y-3">
            <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">
              Verifying Fee Receipt...
            </h3>
            <p className="text-xs text-slate-500 font-mono">{uniqueId}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !uniqueId && (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-slate-200 space-y-3">
            <Receipt className="w-10 h-10 text-primary mx-auto" />
            <h2 className="text-base font-bold text-slate-900">
              Student Fee Receipt Verification
            </h2>
            <p className="text-xs text-slate-600 max-w-sm mx-auto">
              Please enter the unique receipt code below to check live payment
              details.
            </p>
            <form
              onSubmit={handleSearchSubmit}
              className="max-w-md mx-auto flex items-center gap-2 pt-2"
            >
              <input
                type="text"
                placeholder="Enter Fee Receipt Code (UUID)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="flex-1 px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white font-bold text-xs rounded-lg hover:bg-primaryDark transition-colors"
              >
                Verify
              </button>
            </form>
          </div>
        )}

        {/* Light Error State */}
        {!loading && uniqueId && (!isVerified || error) && (
          <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-red-200">
            <div className="bg-red-50 text-red-800 px-4 py-3 flex items-center space-x-2 border-b border-red-200">
              <ShieldAlert className="w-5 h-5 text-red-600 shrink-0" />
              <h2 className="text-sm font-bold">FEE VERIFICATION FAILED</h2>
            </div>
            <div className="p-6 text-center space-y-3">
              <XCircle className="w-10 h-10 text-red-500 mx-auto" />
              <h3 className="text-base font-bold text-slate-900">
                {error || "Fee Receipt Not Found"}
              </h3>
              <p className="text-xs text-slate-600 font-mono">{uniqueId}</p>
              <button
                onClick={() => fetchReceiptData(uniqueId)}
                className="px-4 py-1.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-200 border border-slate-300 inline-flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
            </div>
          </div>
        )}

        {/* Light Verified Success State */}
        {!loading && isVerified && receiptData && (
          <div className="space-y-4">
            {/* Light Emerald Verified Banner */}
            <div className="bg-emerald-50 text-emerald-900 rounded-xl p-4 shadow-sm border border-emerald-200 flex items-center space-x-3">
              <div className="w-9 h-9 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center shrink-0 border border-emerald-200">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-sm sm:text-base font-bold text-emerald-950">
                    VERIFIED FEE PAYMENT RECEIPT
                  </h2>
                  <span className="bg-emerald-200 text-emerald-800 text-[10px] px-2 py-0.5 rounded font-bold">
                    AUTHENTIC
                  </span>
                </div>
                <p className="text-xs text-emerald-700">
                  Receipt No:{" "}
                  <strong className="font-mono text-primary font-bold">
                    {receiptData.receiptNumber}
                  </strong>{" "}
                  • Paid:{" "}
                  <strong className="text-emerald-900 font-bold">
                    {formatCurrency(paidFee)}
                  </strong>
                </p>
              </div>
            </div>

            {/* Official Digital Fee Receipt Container */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {/* Light Blue Header Ribbon */}
              <div className="bg-blue-50/90 text-slate-900 p-4 sm:p-5 border-b border-blue-200">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <img
                      src="/assets/images/logo.png"
                      alt="Logo"
                      className="h-12 w-auto bg-white p-1 rounded-lg object-contain border border-blue-200 shadow-xs"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <div>
                      <h2 className="text-base sm:text-lg font-extrabold uppercase text-primary tracking-wide">
                        DEE NOVO SCHOOL
                      </h2>
                      <p className="text-xs text-slate-600">
                        Affiliated to CBSE • Dumka, Jharkhand
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-[10px] uppercase text-slate-500 font-semibold block">
                      Receipt No
                    </span>
                    <span className="text-xs sm:text-sm font-mono font-bold text-primary">
                      {receiptData.receiptNumber}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 sm:p-5 space-y-4">
                {/* Transaction Attributes Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs">
                  <div>
                    <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                      Receipt Date
                    </span>
                    <p className="font-bold text-slate-800">
                      {formatDate(receiptData.receiptDate)}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                      Payment Mode
                    </span>
                    <p className="font-bold text-slate-800 flex items-center gap-1">
                      <CreditCard className="w-3.5 h-3.5 text-primary" />{" "}
                      {receiptData.paymentMode || "Cash"}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                      Validity
                    </span>
                    <p className="font-bold text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />{" "}
                      {receiptData.status || "Valid"}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                      Fee Status
                    </span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded inline-block mt-0.5 ${
                        studentFee.status === "Paid"
                          ? "bg-emerald-100 text-emerald-800"
                          : studentFee.status === "Partial"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {studentFee.status || "Paid"}
                    </span>
                  </div>
                </div>

                {/* Student Info Box */}
                <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 text-xs space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span className="text-[10px] font-bold text-primary bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                        Adm No: {student.admissionNumber || "N/A"}
                      </span>
                      <h3 className="text-base font-bold text-slate-900 mt-1">
                        {student.studentName || "N/A"}
                      </h3>
                    </div>
                    <div className="text-right text-slate-500 font-medium shrink-0">
                      System ID: #{student.id || receiptData.studentId}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-200 text-slate-700">
                    <div>
                      <strong className="text-slate-900">Class & Sec:</strong>{" "}
                      {student.class || "N/A"} - {student.section || "N/A"}
                    </div>
                    <div>
                      <strong className="text-slate-900">Roll No:</strong>{" "}
                      {student.rollNumber || "N/A"}
                    </div>
                    <div>
                      <strong className="text-slate-900">Session:</strong>{" "}
                      {student.session || "N/A"}
                    </div>
                    <div>
                      <strong className="text-slate-900">Father:</strong>{" "}
                      {student.fatherName || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Fee Breakdown Table */}
                <div className="space-y-2 text-xs">
                  <h4 className="font-bold text-slate-900 flex items-center gap-1.5">
                    <FileCheck className="w-4 h-4 text-primary" /> Itemized
                    Payment Breakdown
                  </h4>

                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-left text-xs text-slate-700">
                      <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] border-b border-slate-200">
                        <tr>
                          <th className="py-2.5 px-3">Fee Period</th>
                          <th className="py-2.5 px-3">Fee Structure</th>
                          <th className="py-2.5 px-3 text-right">
                            Total Payable
                          </th>
                          <th className="py-2.5 px-3 text-right">
                            Amount Paid
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        <tr className="bg-white">
                          <td className="py-2.5 px-3 font-bold text-slate-900">
                            {studentFee.month || "Fee Collection"}
                          </td>
                          <td className="py-2.5 px-3 font-medium">
                            {feeStructure.title ||
                              `Class ${student.class || ""}`}
                            {feeStructure.frequency
                              ? ` (${feeStructure.frequency})`
                              : ""}
                          </td>
                          <td className="py-2.5 px-3 text-right font-medium text-slate-800">
                            {formatCurrency(totalFee)}
                          </td>
                          <td className="py-2.5 px-3 text-right font-bold text-emerald-700">
                            {formatCurrency(paidFee)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 text-center">
                      <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                        Total Fee
                      </span>
                      <span className="font-bold text-slate-800">
                        {formatCurrency(totalFee)}
                      </span>
                    </div>
                    <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                      <span className="text-[10px] text-emerald-700 font-bold uppercase block">
                        Amount Paid
                      </span>
                      <span className="font-bold text-emerald-900">
                        {formatCurrency(paidFee)}
                      </span>
                    </div>
                    <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-center">
                      <span className="text-[10px] text-amber-700 font-semibold uppercase block">
                        Balance Due
                      </span>
                      <span className="font-bold text-amber-900">
                        {formatCurrency(dueFee)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Attachments Section */}
                {attachments && attachments.length > 0 && (
                  <div className="space-y-1.5 pt-1 text-xs">
                    <h4 className="font-bold text-slate-800 flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5 text-primary" /> Payment
                      Proof Attachments ({attachments.length})
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {attachments.map((file) => (
                        <a
                          key={file.id}
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-2 bg-slate-50 hover:bg-blue-50 border border-slate-200 rounded-lg transition-colors text-xs"
                        >
                          <div className="flex items-center space-x-2 truncate">
                            <Paperclip className="w-3.5 h-3.5 text-primary shrink-0" />
                            <span className="font-semibold text-slate-800 truncate">
                              {file.fileName}
                            </span>
                            <span className="text-[10px] text-slate-400">
                              ({formatFileSize(file.fileSizeBytes)})
                            </span>
                          </div>
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Footer Stamp */}
                <div className="pt-3 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
                  <div className="flex items-center space-x-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <span>
                      Verified via Dee Novo Live Registry •{" "}
                      {verifiedAt || "Just now"}
                    </span>
                  </div>
                  {receiptData.generatedByUser && (
                    <div className="text-right">
                      <span>
                        Issued By:{" "}
                        <strong className="text-slate-700">
                          {receiptData.generatedByUser.fullName}
                        </strong>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Light Disclaimer */}
            <div className="bg-blue-50 text-blue-900 rounded-lg p-3 text-xs flex items-start space-x-2 border border-blue-200">
              <Lock className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-blue-950">Financial Note:</span>{" "}
                Receipt{" "}
                <strong className="font-mono text-primary">
                  {receiptData.receiptNumber}
                </strong>{" "}
                is officially recorded in Dee Novo School database.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyStudentFee;
